import React from 'react';
import { useAsync } from 'react-use';
import { getCheckoutSessionId } from '@wix/cashier-common/dist/src/utils/checkoutSessionId';
import { Severity } from '@sentry/browser';
import { WidgetInternalProps } from '../../types/WidgetProps';
import { logGeneralError } from '../../utils/sentry';
import { InteractionName } from '../../types/InteractionName';
import { createI18nInstance, setI18nInstance } from '../../utils/i18n';
import { DH } from '../../utils/dataHooks';
import { WidgetRoot } from './root';

/**
 * In this Component we load top-level resources that we need
 * to initialize even before the Redux store.
 */
export function WidgetRootResources(props: WidgetInternalProps) {
  const experiments = useAsync(
    () =>
      props.preloadingData.experiments.ready().then(() => {
        props.fedopsLogger.interactionEnded(InteractionName.LoadExperiments);
        return props.preloadingData.experiments.all();
      }),
    [props.preloadingData.experiments],
  );

  const sessionId = useAsync(
    () =>
      getCheckoutSessionId(props.meta.siteId, (e) =>
        logGeneralError({
          message: 'getCheckoutSessionId throws error',
          payload: { e },
          severity: Severity.Info,
          fingerprint: ['getCheckoutSessionId throws'],
        }),
      ),
    [props.meta.siteId],
  );

  const i18nInstance = useAsync(
    () =>
      createI18nInstance(props.locale).then((i) => {
        setI18nInstance(i);
        return i;
      }),
    [props.locale],
  );

  if (!experiments.value || !sessionId.value || !i18nInstance.value) {
    return null;
  }

  return (
    <div data-hook={DH.Widget.RootWrapper}>
      <WidgetRoot {...props} sessionId={sessionId.value} i18nInstance={i18nInstance.value} />
    </div>
  );
}
WidgetRootResources.displayName = 'WidgetRootResources';
